import { createSelector } from 'reselect';
import colors from '../components/CommonStyle/colors';
import { getDates, dateIsInRange } from '../helpers/sharedFunction';
import moment from 'moment';

export const totalLeads = createSelector(
  (state) => state.DailySaleInsights.insights,
  (insights) =>
    insights.map((insight) => insight.leads).reduce((a, b) => a + b, 0),
);

export const totalPositives = createSelector(
  (state) => state.DailySaleInsights.insights,
  (insights) =>
    insights.map((insight) => insight.positives).reduce((a, b) => a + b, 0),
);

export const totalRefused = createSelector(
  (state) => state.DailySaleInsights.insights,
  (insights) =>
    insights.map((insight) => insight.refused).reduce((a, b) => a + b, 0),
);

export const totalRevenues = createSelector(
  (state) => state.DailySaleInsights.insights,
  (insights) =>
    insights.map((insight) => insight.revenues).reduce((a, b) => a + b, 0),
);

export const totalPositiveQualifiedCosts = createSelector(
  (state) => state.DailySaleInsights.insights,
  (insights) =>
    insights
      .map((insight) => insight.positiveQualifiedCosts)
      .reduce((a, b) => a + b, 0),
);

export const totalSupplierCosts = createSelector(
  (state) => state.DailySaleInsights.insights,
  (insights) =>
    insights
      .map((insight) => insight.totalSupplierCosts)
      .reduce((a, b) => a + b, 0),
);

export const totalCosts = createSelector(
  (state) => state.DailySaleInsights.insights,
  (insights) =>
    insights.map((insight) => insight.totalCosts).reduce((a, b) => a + b, 0),
);

export const selectDailyInsightsByMonetizationOrder = (monetizationOrderId) =>
  createSelector(
    (state) => state.DailySaleInsights.insights,
    (insights) =>
      insights.filter(
        (insight) => insight.monetizationOrderId === monetizationOrderId,
      ),
  );

export const totalLeadsByMonetizationOrder = (monetizationOrderId) =>
  createSelector(
    selectDailyInsightsByMonetizationOrder(monetizationOrderId),
    (insights) =>
      insights.map((insight) => insight.leads).reduce((a, b) => a + b, 0),
  );

export const totalPositivesByMonetizationOrder = (monetizationOrderId) =>
  createSelector(
    selectDailyInsightsByMonetizationOrder(monetizationOrderId),
    (insights) =>
      insights.map((insight) => insight.positives).reduce((a, b) => a + b, 0),
  );

export const totalRefusedByMonetizationOrder = (monetizationOrderId) =>
  createSelector(
    selectDailyInsightsByMonetizationOrder(monetizationOrderId),
    (insights) =>
      insights.map((insight) => insight.refused).reduce((a, b) => a + b, 0),
  );

export const totalRevenuesByMonetizationOrder = (monetizationOrderId) =>
  createSelector(
    selectDailyInsightsByMonetizationOrder(monetizationOrderId),
    (insights) =>
      insights.map((insight) => insight.revenues).reduce((a, b) => a + b, 0),
  );

export const totalPositiveQualifiedCostsByMonetizationOrder = (
  monetizationOrderId,
) =>
  createSelector(
    selectDailyInsightsByMonetizationOrder(monetizationOrderId),
    (insights) =>
      insights
        .map((insight) => insight.positiveQualifiedCosts)
        .reduce((a, b) => a + b, 0),
  );

export const totalSupplierCostsByMonetizationOrder = (monetizationOrderId) =>
  createSelector(
    selectDailyInsightsByMonetizationOrder(monetizationOrderId),
    (insights) =>
      insights
        .map((insight) => insight.totalSupplierCosts)
        .reduce((a, b) => a + b, 0),
  );

export const totalCostsByMonetizationOrder = (monetizationOrderId) =>
  createSelector(
    selectDailyInsightsByMonetizationOrder(monetizationOrderId),
    (insights) =>
      insights.map((insight) => insight.totalCosts).reduce((a, b) => a + b, 0),
  );

export const drawSeriesDailyGraph = (dateRange, t) =>
  createSelector(
    (state) => state.DailySaleInsights.insights,
    (insights) => {
      const { startDate, endDate } = dateRange;

      const dates = getDates(startDate.toDate(), endDate.toDate());

      let dataGraph = {
        series: [
          {
            name: t('Revenues'),
            data: [],
            type: 'line',
            color: colors.success,
          },
          {
            name: t('Leads'),
            data: [],
            type: 'line',
            color: colors.dark,
          },
        ],
        max: -9999999999,
        min: 99999999999,
      };

      dates.forEach((day) => {
        const startDay = moment(day).utc().startOf('day').toDate();
        const endDay = moment(day).utc().endOf('day').toDate();

        const dailyInsights = insights.filter((insight) =>
          dateIsInRange(new Date(insight.date), startDay, endDay),
        );

        const supplierCosts = dailyInsights
          .map((insight) => insight.totalSupplierCosts)
          .reduce((a, b) => a + b, 0);

        const leads = dailyInsights
          .map((insight) => insight.leads)
          .reduce((a, b) => a + b, 0);

        dataGraph.series[0].data.push([day.getTime(), supplierCosts]);
        dataGraph.series[1].data.push([day.getTime(), leads]);

        dataGraph.max = Math.max(dataGraph.max, supplierCosts);
        dataGraph.min = Math.min(dataGraph.min, supplierCosts);
      });
      return dataGraph;
    },
  );
